import React, { useState, useEffect, useRef } from "react";
import {
  listFreelancerProfiles,
  createFreelancerProfile, updateFreelancerProfile,
  activateFreelancerProfile, duplicateFreelancerProfile,
  deleteFreelancerProfile, parseCV,
} from "../api";

function apiErr(err) {
  const d = err.response?.data;
  if (typeof d?.error === "string") return d.error;
  return err.message || "Something went wrong";
}

const EMPTY_FORM = {
  profile_name: "", full_name: "", professional_title: "",
  email: "", phone: "", location: "", timezone: "",
  company_name: "", company_website: "", company_description: "", team_size: "", company_founded_year: "",
  bio: "", years_of_experience: "", hourly_rate: "", availability: "",
  primary_skills: "", secondary_skills: "", industries: "", tools_technologies: "",
  portfolio_url: "", linkedin_url: "", github_url: "", website_url: "",
  upwork_profile: "", freelancer_profile: "", fiverr_profile: "", guru_profile: "",
  preferred_project_size: "", preferred_project_duration: "", work_style: "",
  certifications: "", education: "", notable_clients: "", success_stories: "", case_studies: "",
  proposal_tone: "professional", unique_value_proposition: "", signature: "", call_to_action: "",
};

function arrToStr(v) {
  if (!v) return "";
  if (Array.isArray(v)) {
    return v.map(item => {
      if (typeof item === "object" && item !== null) {
        // Convert objects like {degree: "BS", institution: "MIT"} to readable string
        return Object.values(item).filter(Boolean).join(" - ");
      }
      return String(item);
    }).join(", ");
  }
  return String(v);
}
function strToArr(s) {
  return (s || "").split(",").map(x => x.trim()).filter(Boolean);
}
function profileToForm(p) {
  return {
    ...EMPTY_FORM, ...p,
    primary_skills: arrToStr(p.primary_skills),
    secondary_skills: arrToStr(p.secondary_skills),
    industries: arrToStr(p.industries),
    certifications: arrToStr(p.certifications),
    education: arrToStr(p.education),
    notable_clients: arrToStr(p.notable_clients),
    success_stories: arrToStr(p.success_stories),
    years_of_experience: p.years_of_experience || "",
    hourly_rate: p.hourly_rate || "",
  };
}
function formToPayload(f) {
  return {
    ...f,
    primary_skills: strToArr(f.primary_skills),
    secondary_skills: strToArr(f.secondary_skills),
    industries: strToArr(f.industries),
    certifications: strToArr(f.certifications),
    education: strToArr(f.education),
    notable_clients: strToArr(f.notable_clients),
    success_stories: strToArr(f.success_stories),
    years_of_experience: f.years_of_experience ? parseInt(f.years_of_experience) : null,
    hourly_rate: f.hourly_rate ? parseFloat(f.hourly_rate) : null,
  };
}

// ── Profile Card ─────────────────────────────────────────────────────────────
function ProfileCard({ profile, isSelected, onSelect, onActivate, onDuplicate, onDelete, canDelete }) {
  return (
    <div
      onClick={onSelect}
      className={`relative p-4 rounded-xl border cursor-pointer transition-all ${
        isSelected
          ? "border-cyan-500/60 bg-cyan-500/10"
          : "border-white/10 bg-white/[0.03] hover:border-white/25"
      }`}
    >
      {profile.is_active && (
        <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          ACTIVE
        </span>
      )}
      <p className="text-white font-semibold text-sm pr-12 truncate">{profile.profile_name}</p>
      <p className="text-gray-500 text-xs mt-0.5 truncate">{profile.professional_title || "No title set"}</p>
      {profile.primary_skills?.length > 0 && (
        <p className="text-gray-600 text-[10px] mt-1 truncate">
          {(Array.isArray(profile.primary_skills) ? profile.primary_skills : []).slice(0, 3).join(", ")}
        </p>
      )}
      <div className="flex gap-1.5 mt-3">
        {!profile.is_active && (
          <button onClick={e => { e.stopPropagation(); onActivate(); }}
            className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-all">
            Set Active
          </button>
        )}
        <button onClick={e => { e.stopPropagation(); onDuplicate(); }}
          className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-white/5 text-gray-400 hover:bg-white/10 transition-all">
          Duplicate
        </button>
        {canDelete && (
          <button onClick={e => { e.stopPropagation(); onDelete(); }}
            className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all">
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

// ── CV Upload ─────────────────────────────────────────────────────────────────
function CVUploader({ onParsed, onMsg }) {
  const fileRef = useRef();
  const [parsing, setParsing] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ["pdf", "docx", "doc", "png", "jpg", "jpeg", "webp"];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!allowed.includes(ext)) {
      onMsg("Unsupported file type. Use PDF, DOCX, DOC, PNG, or JPG.", "error");
      return;
    }
    setParsing(true);
    onMsg("Parsing CV with AI...", "info");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await parseCV(fd);
      onParsed(data.profile_data);
      onMsg("CV parsed! Review and save your profile.", "success");
    } catch (e) {
      onMsg(apiErr(e), "error");
    } finally {
      setParsing(false);
    }
  };

  return (
    <div
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
      onClick={() => !parsing && fileRef.current?.click()}
      className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${
        parsing ? "border-cyan-500/40 bg-cyan-500/5" : "border-white/15 hover:border-cyan-500/40 hover:bg-cyan-500/5"
      }`}
    >
      <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp" className="hidden"
        onChange={e => handleFile(e.target.files[0])} />
      {parsing ? (
        <div className="flex flex-col items-center gap-2">
          <svg className="w-8 h-8 text-cyan-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          <p className="text-cyan-300 text-sm font-medium">AI is reading your CV...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center">
            <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
            </svg>
          </div>
          <p className="text-white font-medium text-sm">Drop your CV here or click to upload</p>
          <p className="text-gray-500 text-xs">PDF, DOCX, DOC, PNG, JPG · AI auto-fills the form</p>
        </div>
      )}
    </div>
  );
}

// ── Main Editor ───────────────────────────────────────────────────────────────
export default function FreelancerProfileEditor({ onClose, onSave }) {
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgTone, setMsgTone] = useState("info");
  const [showCVUpload, setShowCVUpload] = useState(false);

  const showMsg = (text, tone = "info") => { setMsg(text); setMsgTone(tone); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await listFreelancerProfiles();
      setProfiles(data);
      if (data.length > 0) {
        const active = data.find(p => p.is_active) || data[0];
        setSelectedId(active.id);
        setFormData(profileToForm(active));
        setIsNew(false);
      } else {
        setIsNew(true);
        setFormData({ ...EMPTY_FORM, profile_name: "My Profile" });
      }
    } catch (e) {
      showMsg(apiErr(e), "error");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const selectProfile = (p) => {
    setSelectedId(p.id);
    setFormData(profileToForm(p));
    setIsNew(false);
    setMsg("");
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCVParsed = (data) => {
    const merged = { ...formData };
    Object.entries(data).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) {
        if (Array.isArray(v)) {
          merged[k] = v.map(item => {
            if (typeof item === "object" && item !== null) {
              return Object.values(item).filter(Boolean).join(" - ");
            }
            return String(item);
          }).join(", ");
        } else if (typeof v === "object") {
          merged[k] = Object.values(v).filter(Boolean).join(" - ");
        } else {
          merged[k] = String(v || "");
        }
      }
    });
    if (data.profile_name) merged.profile_name = data.profile_name;
    setFormData(merged);
    setShowCVUpload(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name?.trim() && !formData.professional_title?.trim()) {
      showMsg("Please enter at least a name or professional title.", "error");
      return;
    }
    setSaving(true);
    showMsg("Saving...", "info");
    try {
      const payload = formToPayload(formData);
      if (isNew) {
        const { data } = await createFreelancerProfile(payload);
        setProfiles(prev => [...prev, data.profile]);
        setSelectedId(data.profile.id);
        setIsNew(false);
        showMsg("Profile created!", "success");
      } else {
        const { data } = await updateFreelancerProfile(selectedId, payload);
        setProfiles(prev => prev.map(p => p.id === selectedId ? data.profile : p));
        showMsg("Profile saved!", "success");
      }
      onSave?.();
    } catch (e) {
      showMsg(apiErr(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await activateFreelancerProfile(id);
      await load();
      showMsg("Profile set as active.", "success");
    } catch (e) { showMsg(apiErr(e), "error"); }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await duplicateFreelancerProfile(id);
      setProfiles(prev => [...prev, data.profile]);
      setSelectedId(data.profile.id);
      setFormData(profileToForm(data.profile));
      setIsNew(false);
      showMsg("Profile duplicated. Edit and save.", "success");
    } catch (e) { showMsg(apiErr(e), "error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this profile?")) return;
    try {
      await deleteFreelancerProfile(id);
      const updated = profiles.filter(p => p.id !== id);
      setProfiles(updated);
      if (updated.length > 0) {
        const next = updated.find(p => p.is_active) || updated[0];
        setSelectedId(next.id);
        setFormData(profileToForm(next));
      } else {
        setIsNew(true);
        setFormData({ ...EMPTY_FORM, profile_name: "My Profile" });
        setSelectedId(null);
      }
      showMsg("Profile deleted.", "success");
    } catch (e) { showMsg(apiErr(e), "error"); }
  };

  const I = "w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 text-sm";
  const L = "block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider";
  const TA = `${I} resize-y`;
  const banner = msgTone === "error" ? "border-red-500/40 bg-red-950/50 text-red-200"
    : msgTone === "success" ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-100"
    : "border-white/10 bg-white/5 text-gray-200";

  if (loading) return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-8">
        <p className="text-gray-300 text-sm">Loading profiles…</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="w-full max-w-6xl my-6 flex gap-4">

        {/* ── Left: Profile Selector ─────────────────────────────────────── */}
        <div className="w-64 flex-shrink-0 space-y-3">
          <div className="bg-gray-900/90 border border-white/10 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-sm">Profiles</h3>
              <button
                onClick={() => { setIsNew(true); setSelectedId(null); setFormData({ ...EMPTY_FORM, profile_name: `Profile ${profiles.length + 1}` }); setMsg(""); }}
                className="w-7 h-7 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 flex items-center justify-center transition-all"
                title="New profile"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14"/>
                </svg>
              </button>
            </div>

            {profiles.length === 0 && !isNew && (
              <p className="text-gray-600 text-xs text-center py-4">No profiles yet</p>
            )}

            <div className="space-y-2">
              {profiles.map(p => (
                <ProfileCard
                  key={p.id}
                  profile={p}
                  isSelected={selectedId === p.id}
                  onSelect={() => selectProfile(p)}
                  onActivate={() => handleActivate(p.id)}
                  onDuplicate={() => handleDuplicate(p.id)}
                  onDelete={() => handleDelete(p.id)}
                  canDelete={profiles.length > 1}
                />
              ))}
              {isNew && (
                <div className="p-4 rounded-xl border border-cyan-500/40 bg-cyan-500/10">
                  <p className="text-cyan-300 font-semibold text-sm">New Profile</p>
                  <p className="text-gray-500 text-xs mt-0.5">Fill details and save</p>
                </div>
              )}
            </div>
          </div>

          {/* Tips */}
          <div className="bg-gray-900/90 border border-white/10 rounded-2xl p-4">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-3">Tips</p>
            <ul className="space-y-2 text-xs text-gray-600">
              <li className="flex gap-2"><span className="text-cyan-400">◉</span>Create separate profiles for different roles (e.g., Developer vs Designer)</li>
              <li className="flex gap-2"><span className="text-cyan-400">◉</span>The active profile is used when generating proposals</li>
              <li className="flex gap-2"><span className="text-cyan-400">◉</span>Upload a CV to auto-fill fields instantly</li>
            </ul>
          </div>
        </div>

        {/* ── Right: Form ────────────────────────────────────────────────── */}
        <div className="flex-1 bg-gray-900/90 border border-white/10 rounded-2xl overflow-hidden">

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/8 bg-gray-900/95 backdrop-blur">
            <div>
              <h2 className="text-white font-bold text-lg">{isNew ? "New Profile" : (formData.profile_name || "Edit Profile")}</h2>
              <p className="text-gray-500 text-xs mt-0.5">{isNew ? "Create a new freelancer profile" : "Update profile details"}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowCVUpload(v => !v)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  showCVUpload ? "bg-cyan-500/20 border border-cyan-500/40 text-cyan-400" : "bg-white/5 border border-white/10 text-gray-400 hover:border-white/20"
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
                Upload CV
              </button>
              <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] space-y-6">

            {/* CV Upload Panel */}
            {showCVUpload && (
              <div className="p-4 rounded-2xl border border-cyan-500/20 bg-cyan-500/5">
                <p className="text-cyan-300 font-semibold text-sm mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                  </svg>
                  AI CV Parser — auto-fills your profile
                </p>
                <CVUploader onParsed={handleCVParsed} onMsg={showMsg} />
              </div>
            )}

            {/* Message */}
            {msg && (
              <div className={`p-3 rounded-xl border text-sm ${banner}`}>{msg}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Profile Name */}
              <div className="p-4 rounded-xl border border-white/8 bg-white/[0.02]">
                <label className={L}>Profile Name</label>
                <input name="profile_name" value={formData.profile_name} onChange={handleChange}
                  placeholder="e.g., Full-Stack Dev, UI/UX Designer, WordPress Expert"
                  className={I} />
                <p className="text-gray-600 text-xs mt-1.5">Give this profile a descriptive name to differentiate it from others</p>
              </div>

              {/* Personal */}
              <section>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-cyan-500/15 flex items-center justify-center text-cyan-400 text-base">👤</span>
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className={L}>Full Name *</label><input name="full_name" value={formData.full_name} onChange={handleChange} placeholder="Jane Doe" className={I} /></div>
                  <div><label className={L}>Professional Title *</label><input name="professional_title" value={formData.professional_title} onChange={handleChange} placeholder="Senior Full-Stack Developer" className={I} /></div>
                  <div><label className={L}>Email</label><input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="jane@example.com" className={I} /></div>
                  <div><label className={L}>Phone</label><input name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 555 123 4567" className={I} /></div>
                  <div><label className={L}>Location</label><input name="location" value={formData.location} onChange={handleChange} placeholder="New York, USA" className={I} /></div>
                  <div><label className={L}>Timezone</label><input name="timezone" value={formData.timezone} onChange={handleChange} placeholder="EST (UTC-5)" className={I} /></div>
                </div>
              </section>

              {/* Company */}
              <section>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-orange-500/15 flex items-center justify-center text-orange-400 text-base">🏢</span>
                  Company / Agency <span className="text-xs text-gray-600 font-normal">(optional)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className={L}>Company Name</label><input name="company_name" value={formData.company_name || ""} onChange={handleChange} placeholder="Your Company LLC" className={I} /></div>
                  <div><label className={L}>Company Website</label><input type="url" name="company_website" value={formData.company_website || ""} onChange={handleChange} placeholder="https://yourcompany.com" className={I} /></div>
                  <div><label className={L}>Team Size</label>
                    <select name="team_size" value={formData.team_size || ""} onChange={handleChange} className={I}>
                      <option value="">Select...</option>
                      <option>Solo Freelancer</option><option>2-5</option><option>6-20</option><option>21-50</option><option>50+</option>
                    </select>
                  </div>
                  <div><label className={L}>Founded Year</label><input type="number" name="company_founded_year" value={formData.company_founded_year || ""} onChange={handleChange} placeholder="2020" className={I} /></div>
                  <div className="md:col-span-2"><label className={L}>Company Description</label><textarea name="company_description" value={formData.company_description || ""} onChange={handleChange} placeholder="What does your company do?" className={`${TA} min-h-[70px]`} /></div>
                </div>
              </section>

              {/* Summary */}
              <section>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-purple-500/15 flex items-center justify-center text-purple-400 text-base">💼</span>
                  Professional Summary
                </h3>
                <div className="space-y-4">
                  <div><label className={L}>Bio</label><textarea name="bio" value={formData.bio} onChange={handleChange} placeholder="2-3 sentences about your expertise..." className={`${TA} min-h-[90px]`} /></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><label className={L}>Years Experience</label><input type="number" name="years_of_experience" value={formData.years_of_experience} onChange={handleChange} placeholder="5" min="0" className={I} /></div>
                    <div><label className={L}>Hourly Rate ($)</label><input type="number" name="hourly_rate" value={formData.hourly_rate} onChange={handleChange} placeholder="75" min="0" className={I} /></div>
                    <div><label className={L}>Availability</label>
                      <select name="availability" value={formData.availability} onChange={handleChange} className={I}>
                        <option value="">Select...</option>
                        <option>Full-time (40+ hrs/week)</option>
                        <option>Part-time (20-30 hrs/week)</option>
                        <option>As needed (flexible)</option>
                        <option>Not available</option>
                      </select>
                    </div>
                  </div>
                  <div><label className={L}>Unique Value Proposition</label><textarea name="unique_value_proposition" value={formData.unique_value_proposition || ""} onChange={handleChange} placeholder="What makes you uniquely different from competitors?" className={`${TA} min-h-[70px]`} /></div>
                </div>
              </section>

              {/* Skills */}
              <section>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-teal-500/15 flex items-center justify-center text-teal-400 text-base">⚡</span>
                  Skills & Expertise
                </h3>
                <div className="space-y-4">
                  <div><label className={L}>Primary Skills <span className="text-gray-600 normal-case font-normal">(comma-separated)</span></label><input name="primary_skills" value={formData.primary_skills} onChange={handleChange} placeholder="React, Node.js, Python, PostgreSQL" className={I} /></div>
                  <div><label className={L}>Secondary Skills</label><input name="secondary_skills" value={formData.secondary_skills} onChange={handleChange} placeholder="Docker, AWS, GraphQL" className={I} /></div>
                  <div><label className={L}>Industries</label><input name="industries" value={formData.industries} onChange={handleChange} placeholder="E-commerce, FinTech, SaaS" className={I} /></div>
                  <div><label className={L}>Tools & Technologies</label><input name="tools_technologies" value={formData.tools_technologies || ""} onChange={handleChange} placeholder="VS Code, Figma, Jira, Docker" className={I} /></div>
                </div>
              </section>

              {/* Portfolio */}
              <section>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center text-blue-400 text-base">🔗</span>
                  Portfolio & Links
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className={L}>Portfolio URL</label><input type="url" name="portfolio_url" value={formData.portfolio_url} onChange={handleChange} placeholder="https://yourportfolio.com" className={I} /></div>
                  <div><label className={L}>LinkedIn</label><input type="url" name="linkedin_url" value={formData.linkedin_url} onChange={handleChange} placeholder="https://linkedin.com/in/..." className={I} /></div>
                  <div><label className={L}>GitHub</label><input type="url" name="github_url" value={formData.github_url} onChange={handleChange} placeholder="https://github.com/..." className={I} /></div>
                  <div><label className={L}>Website</label><input type="url" name="website_url" value={formData.website_url} onChange={handleChange} placeholder="https://yoursite.com" className={I} /></div>
                  <div><label className={L}>Upwork</label><input name="upwork_profile" value={formData.upwork_profile} onChange={handleChange} placeholder="Upwork profile URL" className={I} /></div>
                  <div><label className={L}>Fiverr</label><input name="fiverr_profile" value={formData.fiverr_profile} onChange={handleChange} placeholder="Fiverr profile URL" className={I} /></div>
                </div>
              </section>

              {/* Credentials */}
              <section>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center text-amber-400 text-base">🏆</span>
                  Achievements & Credentials
                </h3>
                <div className="space-y-4">
                  <div><label className={L}>Certifications <span className="text-gray-600 normal-case font-normal">(comma-separated)</span></label><input name="certifications" value={formData.certifications} onChange={handleChange} placeholder="AWS Certified, Google Cloud, PMP" className={I} /></div>
                  <div><label className={L}>Education</label><input name="education" value={formData.education} onChange={handleChange} placeholder="BS Computer Science - MIT 2019" className={I} /></div>
                  <div><label className={L}>Notable Clients</label><input name="notable_clients" value={formData.notable_clients} onChange={handleChange} placeholder="Fortune 500 Co, Tech Startup, E-commerce Brand" className={I} /></div>
                  <div><label className={L}>Success Stories</label><textarea name="success_stories" value={formData.success_stories} onChange={handleChange} placeholder="Increased client revenue by 40%, Built platform serving 1M+ users" className={`${TA} min-h-[80px]`} /></div>
                  <div><label className={L}>Case Studies</label><textarea name="case_studies" value={formData.case_studies || ""} onChange={handleChange} placeholder="E-commerce: 200% traffic increase in 6 months" className={`${TA} min-h-[70px]`} /></div>
                </div>
              </section>

              {/* Proposal Customization */}
              <section>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-indigo-500/15 flex items-center justify-center text-indigo-400 text-base">✍️</span>
                  Proposal Settings
                </h3>
                <div className="space-y-4">
                  <div><label className={L}>Default Tone</label>
                    <select name="proposal_tone" value={formData.proposal_tone} onChange={handleChange} className={I}>
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="confident">Confident</option>
                      <option value="consultative">Consultative</option>
                    </select>
                  </div>
                  <div><label className={L}>Custom Signature</label><textarea name="signature" value={formData.signature} onChange={handleChange} placeholder={"Best regards,\nJane Doe\njane@example.com"} className={`${TA} min-h-[90px]`} /></div>
                  <div><label className={L}>Call to Action</label><input name="call_to_action" value={formData.call_to_action} onChange={handleChange} placeholder="Let's schedule a call to discuss your project!" className={I} /></div>
                </div>
              </section>

              {/* Save */}
              <div className="flex gap-3 pt-2 border-t border-white/8">
                <button type="button" onClick={onClose} disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-gray-400 font-semibold hover:bg-white/10 transition-colors disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-emerald-600 text-white font-bold hover:from-cyan-500 hover:to-emerald-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {saving ? (
                    <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Saving…</>
                  ) : (
                    <>{isNew ? "Create Profile" : "Save Profile"}</>
                  )}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
